import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { BiLoaderAlt } from "react-icons/bi";
import { cartFunnelDetails } from "../../../../stores/actions/cart-funnel.actions.types";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MetaHead from "../../../Common/Metahead";

// Helper: recursively checks if every value in the attribution object is null/undefined
const isAttributionEmpty = (attribution) => {
  return Object.values(attribution).every((value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "object") return isAttributionEmpty(value);
    return false;
  });
};

const DetailsCartFunnel = ({ cartFunnelDetails, loading }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [cartFunnel, setCartFunnel] = useState(null);

  const fetchCartFunnelDetails = useCallback(() => {
    if (!id) return;
    cartFunnelDetails({
      id,
      callback: (data) => {
        if (data?.success) setCartFunnel(data?.data?.cardFunnel);
      },
    });
  }, [id, cartFunnelDetails]);

  useEffect(() => {
    fetchCartFunnelDetails();
  }, [fetchCartFunnelDetails]);

  // Flatten all items in the data array
  const selectedItems = cartFunnel?.data
    ? cartFunnel.data.flatMap((obj) => Object.values(obj))
    : [];

  return (
    <div className='mx-auto p-5 text-black dark:text-white dark:bg-boxdark bg-white'>
      <MetaHead title={t("cart-funnel-details")} />
      {loading ? (
        <div className='flex items-center justify-center min-h-96'>
          <BiLoaderAlt className='animate-spin text-4xl text-primary dark:text-white' />
        </div>
      ) : (
        <>
          <h2 className='text-2xl font-bold mb-4'>Cart Funnel Details</h2>

          <div className='mb-6'>
            <p>
              <strong>Email:</strong> {cartFunnel?.email || "N/A"}
            </p>

            <p>
              <strong>Email Sent Count:</strong>{" "}
              {cartFunnel?.emailSentCount ?? 0}
            </p>
            <p>
              <strong>Created At:</strong>{" "}
              {cartFunnel?.createdAt
                ? new Date(cartFunnel.createdAt).toLocaleString()
                : "N/A"}
            </p>
            <p>
              <strong>Updated At:</strong>{" "}
              {cartFunnel?.updatedAt
                ? new Date(cartFunnel.updatedAt).toLocaleString()
                : "N/A"}
            </p>
          </div>

          {selectedItems.length > 0 ? (
            selectedItems.map((item, index) => (
              <div
                key={item._id || index}
                className='mb-6 p-4 border rounded-lg dark:border-gray-600 dark:bg-gray-800 bg-gray-50'
              >
                {item.item_image && (
                  <img
                    src={item.item_image ?? "/not-available2.png"}
                    alt={item.title}
                    className='w-48 h-auto mb-3 rounded'
                  />
                )}
                <h3 className='text-lg font-bold mb-2'>{item.title}</h3>
                <p>
                  <strong>ID:</strong> {item._id}
                </p>
                <p>
                  <strong>SKU:</strong> {item.sku}
                </p>
                <p>
                  <strong>Type:</strong> {item.item_type}
                </p>
                <p>
                  <strong>Class:</strong> {item.item_class}
                </p>
                <p>
                  <strong>Brand:</strong> {item.brand}
                </p>
                <p>
                  <strong>Model Group:</strong> {item.model_group}
                </p>
                <p>
                  <strong>Forging Style:</strong> {item.forging_style}
                </p>
                <p>
                  <strong>Wheel Size:</strong> {item.wheel_size}
                </p>
                <p>
                  <strong>Finish:</strong> {item.finish}
                </p>
                <p>
                  <strong>Offset:</strong> {item.offset}
                </p>
                <p>
                  <strong>Bolt Patterns:</strong> {item.bolt_pattern_1} /{" "}
                  {item.bolt_pattern_2}
                </p>
                <p>
                  <strong>Center Bore:</strong> {item.centerbore}
                </p>
                <p>
                  <strong>Quantity:</strong> {item.quantity}
                </p>
                <p>
                  <strong>Total Price:</strong> ${item.totalPrice}
                </p>
                <p>
                  <strong>Vehicle Info:</strong> {item.vehicleInformation}
                </p>
                <div
                  dangerouslySetInnerHTML={{
                    __html: item.purchase_description,
                  }}
                />
              </div>
            ))
          ) : (
            <p className='text-gray-600 dark:text-gray-300'>
              No items selected for this cart funnel.
            </p>
          )}

          {/* Attribution */}
          <div className='border border-[#eee] dark:border-dark-3 rounded-md p-4 mb-6'>
            <h5 className='font-medium text-dark dark:text-white mb-3'>
              Attribution Data
            </h5>
            {cartFunnel?.attribution &&
            !isAttributionEmpty(cartFunnel.attribution) ? (
              Object.entries(cartFunnel.attribution).map(([key, value]) => (
                <div key={key} className='mb-2'>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    <strong>
                      {key.charAt(0).toUpperCase() + key.slice(1)}:
                    </strong>{" "}
                    {value === null || value === undefined ? (
                      "N/A"
                    ) : typeof value === "object" ? (
                      <span className='block ml-4'>
                        {Object.entries(value).map(([subKey, subValue]) => (
                          <span
                            key={subKey}
                            className='block text-sm text-gray-600 dark:text-gray-400'
                          >
                            <strong>
                              {subKey.charAt(0).toUpperCase() + subKey.slice(1)}
                              :
                            </strong>{" "}
                            {subValue !== null && subValue !== undefined
                              ? String(subValue)
                              : "N/A"}
                          </span>
                        ))}
                      </span>
                    ) : (
                      String(value)
                    )}
                  </p>
                </div>
              ))
            ) : (
              <p className='text-sm text-gray-600 dark:text-gray-400'>
                No attribution data available for this cart funnel.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: state?.app?.visible,
});

export default connect(mapStateToProps, { cartFunnelDetails })(
  DetailsCartFunnel
);
